import { css, html, nothing } from 'lit';
import { api, getBackendUrl } from '../../lib/api.js';
import { deleteVersionsPath, previewRows } from '../../lib/asset-versions.js';
import { authState } from '../../lib/auth.js';
import { authorLabel } from '../../lib/authors.js';
import { BaseComponent } from '../../lib/base-component.js';
import { loadConfig } from '../../lib/config.js';
import { downloadAssetFile } from '../../lib/download.js';
import {
    anyFlattenPending,
    FLATTEN_POLL_MS,
    isFlattenPending,
} from '../../lib/miniature-flatten.js';
import { defaultSelectedVersions } from '../../lib/miniature-versions.js';
import { uploadFile } from '../../lib/upload.js';
import '../shared/app-pins-panel.js';
import '../shared/asset-versions-table.js';
// Side-effect import: registers <miniature-grid-preview>, the shared grid preview
// the commission/assignment reviews use too.
import './miniature-grid-preview.js';

class MiniaturesView extends BaseComponent {
    static properties = {
        _miniatures: { type: Array, state: true },
        _loading: { type: Boolean, state: true },
        _error: { type: String, state: true },
        _searchQuery: { type: String, state: true },
        _miniatureTypeFilter: { type: String, state: true },
        _tagFilter: { type: String, state: true },
        _authorFilter: { type: String, state: true },
        _includeUploads: { type: Boolean, state: true },
        _page: { type: Number, state: true },
        _total: { type: Number, state: true },
        _hasMore: { type: Boolean, state: true },
        _view: { type: String, state: true }, // 'list', 'detail', 'create', 'edit'
        _selectedMiniature: { type: Object, state: true },
        _formData: { type: Object, state: true },
        _saving: { type: Boolean, state: true },
        _versionsBusy: { type: Boolean, state: true },
        _confirmDelete: { type: Boolean, state: true },
        _allFiles: { type: Array, state: true },
        _newSource: { type: String, state: true },
        _dragOver: { type: Boolean, state: true },
        _selectedVersions: { type: Object, state: true },
        _versionDownloading: { type: String, state: true },
        _config: { type: Object, state: true },
    };

    static styles = css`
        :host { display: block; }
        .page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem; }
        .page-header h4 { margin: 0; font-size: 1.5rem; font-weight: 600; }
        .page-subtitle { color: #666; font-size: 0.9rem; margin-bottom: 1.5rem; }
        table { font-size: 0.9rem; }
        .badge-list { display: flex; flex-wrap: wrap; gap: 0.25rem; }
        .miniature-thumb-sm { width: 64px; height: 64px; border-radius: 4px; object-fit: contain; background: #f0f0f0; }
        .miniature-thumb-status { display: flex; align-items: center; justify-content: center; color: #6c757d; }
        .miniature-thumb-status.failed { color: #dc3545; }
        .back-link {
            display: inline-flex; align-items: center; gap: 0.25rem;
            color: #666; text-decoration: none; font-size: 0.9rem; margin-bottom: 1rem;
        }
        .back-link:hover { color: #333; }
        .detail-header { display: flex; align-items: center; gap: 1rem; margin-bottom: 1.5rem; }
        .detail-header-icon {
            width: 64px; height: 64px; border-radius: 8px; background: #f0f0f0;
            display: flex; align-items: center; justify-content: center; overflow: hidden;
        }
        .detail-header-icon img { width: 100%; height: 100%; object-fit: contain; }
        .detail-header-info h4 { margin: 0; font-size: 1.5rem; font-weight: 600; }
        .detail-header-info p { margin: 0; color: #666; font-size: 0.9rem; }
        .detail-section { border: 1px solid #eee; border-radius: 8px; padding: 1.25rem; margin-bottom: 1.25rem; }
        .detail-section h6 { font-weight: 600; margin-bottom: 1rem; }
        .prop-row { display: flex; padding: 0.5rem 0; border-bottom: 1px solid #f0f0f0; }
        .prop-row:last-child { border-bottom: none; }
        .prop-label { width: 120px; font-weight: 500; color: #666; font-size: 0.85rem; flex-shrink: 0; }
        .prop-value { flex: 1; font-size: 0.9rem; }
        .form-panel { max-width: 500px; }
    `;

    constructor() {
        super();
        this._miniatures = [];
        this._loading = true;
        this._error = null;
        this._searchQuery = '';
        this._miniatureTypeFilter = '';
        this._tagFilter = '';
        this._authorFilter = '';
        // Off by default: miniatures whose art is only an app user's upload stay
        // out of the list unless they're asked for.
        this._includeUploads = false;
        this._page = 0;
        this._total = 0;
        this._hasMore = false;
        this._view = 'list';
        this._selectedMiniature = null;
        this._formData = {};
        this._saving = false;
        this._confirmDelete = false;
        this._allFiles = [];
        this._newSource = '';
        this._dragOver = false;
        this._availableSources = [];
        this._selectedVersions = {};
        this._versionDownloading = null;
        this._config = null;
        /** @type {ReturnType<typeof setTimeout> | undefined} */
        this._flattenPollTimer = undefined;
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        clearTimeout(this._flattenPollTimer);
    }

    // While a miniature's image is still being flattened on the server's
    // heavy-job queue, re-read it every few seconds so the image (or the reason
    // the flatten failed) appears without a manual reload.
    updated() {
        clearTimeout(this._flattenPollTimer);
        const waiting =
            (this._view === 'list' && anyFlattenPending(this._miniatures)) ||
            (this._view === 'detail' && isFlattenPending(this._selectedMiniature));
        if (waiting) {
            this._flattenPollTimer = setTimeout(() => this._refreshFlattening(), FLATTEN_POLL_MS);
        }
    }

    async _refreshFlattening() {
        try {
            if (this._view === 'detail' && this._selectedMiniature) {
                const miniature = await api.get(
                    `/api/assets/miniatures/${this._selectedMiniature.id}`,
                );
                this._selectedMiniature = miniature;
                if (!isFlattenPending(miniature)) {
                    // The flatten finished: a new version exists, so select it
                    // and re-read what the apps serve.
                    this._resetVersionSelection();
                    this._pinsPanel()?.reload();
                }
            } else if (this._view === 'list') {
                await this._loadMiniatures({ quiet: true });
            }
        } catch (err) {
            this._error = err.message;
        }
    }

    async connectedCallback() {
        super.connectedCallback();
        this._loadMiniatures();
        loadConfig()
            .then((cfg) => {
                this._config = cfg;
            })
            .catch(() => {});
    }

    // The miniature size vocabulary: footprint occupancy plus the larger PSD canvas
    // (footprint + a 1-cell overflow ring, except Tiny). Mirrors model.MiniatureSizeSpecs.
    _miniatureSizeOptions() {
        return [
            { code: 'T', name: 'Tiny', canvasPx: 400 },
            { code: 'S', name: 'Small', canvasPx: 1200 },
            { code: 'M', name: 'Medium', canvasPx: 1200 },
            { code: 'L', name: 'Large', canvasPx: 1600 },
            { code: 'H', name: 'Huge', canvasPx: 2000 },
            { code: 'G', name: 'Gargantuan', canvasPx: 2400 },
        ];
    }

    // Grid dimensions for a miniature, from the api's size vocabulary
    // (/api/config `miniatureSizes`). Prefers the size code and falls back to
    // size_cells, the same order the detail header uses; a legacy miniature with
    // neither gets 0 footprint cells, which renders a grid with nothing marked
    // because there is genuinely no footprint to mark.
    _gridSpec(miniature) {
        const spec = this._config?.miniatureSizes?.[miniature?.miniature_size];
        return {
            footprintCells: spec?.footprintCells ?? miniature?.size_cells ?? 0,
            canvasCells: spec?.canvasCells ?? 0,
            cellPx: this._config?.miniatureCellPx ?? 0,
        };
    }

    _miniatureSizeName(code) {
        const o = this._miniatureSizeOptions().find((o) => o.code === code);
        return o ? o.name : null;
    }

    // The canvas px the selected size's PSD must flatten to.
    _miniatureSizePx() {
        const o = this._miniatureSizeOptions().find(
            (o) => o.code === this._formData?.miniature_size,
        );
        return o ? o.canvasPx : 400;
    }

    _miniatureTemplateUrl() {
        return this._config?.miniatureTemplateUrls?.[this._formData?.miniature_size] || null;
    }

    /** @param {{ quiet?: boolean }} [options] quiet: refresh without the loading state (polling) */
    async _loadMiniatures({ quiet = false } = {}) {
        if (!quiet) this._loading = true;
        this._error = null;
        try {
            const params = new URLSearchParams();
            params.set('page', String(this._page));
            params.set('pageSize', '50');
            if (this._searchQuery) params.set('query', this._searchQuery);
            if (this._miniatureTypeFilter) params.set('miniature_type', this._miniatureTypeFilter);
            if (this._tagFilter) params.set('tag', this._tagFilter);
            if (this._authorFilter) params.set('author', this._authorFilter);
            if (this._includeUploads) params.set('include_uploads', 'true');
            const data = await api.get(`/api/assets/miniatures?${params}`);
            this._miniatures = data.miniatures || [];
            this._total = data.total || 0;
            this._hasMore = data.hasMore || false;
        } catch (err) {
            this._error = err.message;
        } finally {
            this._loading = false;
        }
    }

    async _openDetail(miniature) {
        this._error = null;
        try {
            this._selectedMiniature = await api.get(`/api/assets/miniatures/${miniature.id}`);
            await this._loadSources();
            this._resetVersionSelection();
            this._view = 'detail';
        } catch (err) {
            this._error = err.message;
        }
    }

    async _loadSources() {
        try {
            const data = await api.get('/api/sources');
            this._availableSources = data.sources || [];
            const defaultSource = this._availableSources.find((s) => s.isDefault);
            if (defaultSource) {
                this._newSource = defaultSource.slug;
            }
        } catch {
            this._availableSources = [];
        }
    }

    // The miniature's versions (issue #217): each made from a layered PSD (or an
    // image) and serving its flattened image.
    _versions() {
        return this._selectedMiniature?.versions || [];
    }

    _pinsPanel() {
        return /** @type {any} */ (this.shadowRoot?.querySelector('app-pins-panel'));
    }

    // Make every app serve one version (the versions table's "Pin for every
    // app").
    async _pinAll(e) {
        await this._pinsPanel()?.setPin(null, {
            source: e.detail.source,
            version: e.detail.version,
        });
    }

    async _downloadInput(e) {
        this._error = null;
        try {
            await downloadAssetFile(this._selectedMiniature.id, {
                source: e.detail.source,
                version: e.detail.version,
                kind: 'input',
            });
        } catch (err) {
            this._error = err.message;
        }
    }

    _resetVersionSelection() {
        this._selectedVersions = defaultSelectedVersions(
            this._sourceGroups(previewRows(this._versions())),
        );
    }

    async _downloadVersion(source, file) {
        this._versionDownloading = source;
        this._error = null;
        try {
            await downloadAssetFile(this._selectedMiniature.id, { source, version: file.version });
        } catch (err) {
            this._error = err.message;
        } finally {
            this._versionDownloading = null;
        }
    }

    _backToList() {
        this._view = 'list';
        this._selectedMiniature = null;
        this._confirmDelete = false;
        this._selectedVersions = {};
    }

    async _startCreate() {
        await this._loadSources();
        const defaultSource = this._availableSources.find((s) => s.isDefault);
        this._formData = {
            slug: '',
            miniature_type: 'pc',
            miniature_size: 'M',
            tags: '',
            source: defaultSource?.slug || '',
            file: null,
        };
        this._view = 'create';
        this._error = null;
    }

    _startEdit() {
        const t = this._selectedMiniature;
        this._view = 'edit';
        this._formData = {
            miniature_type: t.miniature_type || 'pc',
            tags: (t.tags || []).join(', '),
        };
        this._error = null;
    }

    async _submitCreate(e) {
        e.preventDefault();
        this._saving = true;
        this._error = null;
        try {
            const upload = await uploadFile(this._formData.file);
            const form = new FormData();
            form.append('slug', this._formData.slug);
            form.append('miniature_type', this._formData.miniature_type);
            form.append('miniature_size', this._formData.miniature_size);
            if (this._formData.tags) form.append('tags', this._formData.tags);
            form.append('source', this._formData.source);
            form.append('upload_id', upload.upload_id);
            await api.upload('/api/assets/miniatures', form);
            this._view = 'list';
            this._loadMiniatures();
        } catch (err) {
            this._error = err.message;
        } finally {
            this._saving = false;
        }
    }

    async _submitEdit(e) {
        e.preventDefault();
        this._saving = true;
        this._error = null;
        try {
            const tags = this._formData.tags
                ? this._formData.tags
                      .split(',')
                      .map((t) => t.trim())
                      .filter(Boolean)
                : [];
            await api.put(`/api/assets/miniatures/${this._selectedMiniature.id}`, {
                miniature_type: this._formData.miniature_type,
                tags,
            });
            this._selectedMiniature = await api.get(
                `/api/assets/miniatures/${this._selectedMiniature.id}`,
            );
            this._view = 'detail';
            this._loadMiniatures();
        } catch (err) {
            this._error = err.message;
        } finally {
            this._saving = false;
        }
    }

    // Delete one version of a source, or the whole source (issue #199), then
    // reload the miniature: its current image, versions and what apps serve may
    // change.
    async _deleteVersions(e) {
        const { source, version } = e.detail;
        this._versionsBusy = true;
        this._error = null;
        try {
            await api.delete(deleteVersionsPath(this._selectedMiniature.id, source, version));
            await this._openDetail(this._selectedMiniature);
            this._pinsPanel()?.reload();
        } catch (err) {
            this._error = err.message;
        } finally {
            this._versionsBusy = false;
        }
    }

    async _setCurrent(e) {
        this._versionsBusy = true;
        this._error = null;
        try {
            await api.put(
                `/api/assets/${this._selectedMiniature.id}/versions/${e.detail.versionId}/set-current`,
            );
            await this._openDetail(this._selectedMiniature);
        } catch (err) {
            this._error = err.message;
        } finally {
            this._versionsBusy = false;
        }
    }

    // Credit a version to its author (issue #215), or clear it, then reread the
    // miniature for its versions, keeping the versions chosen on the page.
    async _setAuthor(e) {
        const { versionId, body } = e.detail;
        const id = this._selectedMiniature.id;
        this._versionsBusy = true;
        this._error = null;
        try {
            await api.put(`/api/assets/${id}/versions/${versionId}/author`, body);
            this._selectedMiniature = await api.get(`/api/assets/miniatures/${id}`);
        } catch (err) {
            this._error = err.message;
        } finally {
            this._versionsBusy = false;
        }
    }

    // Publish the miniature to the freelancer catalog, or take it out (issue #205).
    async _toggleCatalog(e) {
        const inCatalog = e.target.checked;
        this._saving = true;
        this._error = null;
        try {
            const updated = await api.put(`/api/assets/miniatures/${this._selectedMiniature.id}`, {
                in_catalog: inCatalog,
            });
            this._selectedMiniature = {
                ...this._selectedMiniature,
                in_catalog: updated.in_catalog,
                catalog_published_at: updated.catalog_published_at,
            };
        } catch (err) {
            e.target.checked = !inCatalog;
            this._error = err.message;
        } finally {
            this._saving = false;
        }
    }

    async _deleteMiniature() {
        this._saving = true;
        this._error = null;
        try {
            await api.delete(`/api/assets/miniatures/${this._selectedMiniature.id}`);
            this._view = 'list';
            this._selectedMiniature = null;
            this._confirmDelete = false;
            this._loadMiniatures();
        } catch (err) {
            this._error = err.message;
        } finally {
            this._saving = false;
        }
    }

    // Square grid footprints a miniature can occupy, by side length in cells.
    // Mirrors the API's 1–4 validation and the D&D size each maps to.
    _sizeCellOptions() {
        return [
            { value: 1, label: '1 cell · Medium' },
            { value: 2, label: '2 cells · Large' },
            { value: 3, label: '3 cells · Huge' },
            { value: 4, label: '4 cells · Gargantuan' },
        ];
    }

    _sizeLabel(n) {
        const opt = this._sizeCellOptions().find((o) => o.value === n);
        return opt ? opt.label : `${n} cells`;
    }

    _sourceGroups(files) {
        const groups = {};
        for (const f of files) {
            if (!groups[f.source])
                groups[f.source] = { source: f.source, files: [], current: null };
            groups[f.source].files.push(f);
            if (f.is_current) groups[f.source].current = f;
        }
        for (const g of Object.values(groups)) {
            g.files.sort((a, b) => b.version - a.version);
        }
        return Object.values(groups);
    }

    _unusedSources(miniature) {
        const usedSources = new Set(
            (miniature.versions || []).filter((v) => v.is_current).map((v) => v.source),
        );
        return this._availableSources.filter((s) => !usedSources.has(s.slug));
    }

    async _uploadNewImage(e) {
        const file = e.target.files[0];
        if (!file || !this._newSource) return;
        this._saving = true;
        this._error = null;
        try {
            const upload = await uploadFile(file);
            const form = new FormData();
            form.append('upload_id', upload.upload_id);
            form.append('source', this._newSource);
            const accessToken = await authState.getAccessToken();
            const resp = await fetch(
                `${getBackendUrl()}/api/assets/miniatures/${this._selectedMiniature.id}/image`,
                {
                    method: 'PUT',
                    headers: { Authorization: `Bearer ${accessToken}` },
                    body: form,
                },
            );
            if (!resp.ok) {
                const data = await resp.json().catch(() => ({}));
                throw new Error(data.message || `Upload error: ${resp.status}`);
            }
            this._selectedMiniature = await api.get(
                `/api/assets/miniatures/${this._selectedMiniature.id}`,
            );
            await this._loadPins(this._selectedMiniature.id);
            this._resetVersionSelection();
            this._newSource = '';
        } catch (err) {
            this._error = err.message;
        } finally {
            this._saving = false;
        }
    }

    _handleSearch(e) {
        this._searchQuery = e.target.value;
        this._page = 0;
        this._loadMiniatures();
    }
    _handleTypeFilter(e) {
        this._miniatureTypeFilter = e.target.value;
        this._page = 0;
        this._loadMiniatures();
    }
    _handleTagFilter(e) {
        this._tagFilter = e.target.value.trim().toLowerCase();
        this._page = 0;
        this._loadMiniatures();
    }
    _handleAuthorFilter(e) {
        this._authorFilter = e.target.value.trim();
        this._page = 0;
        this._loadMiniatures();
    }
    _handleIncludeUploads(e) {
        this._includeUploads = e.target.checked;
        this._page = 0;
        this._loadMiniatures();
    }
    _handlePrevPage() {
        if (this._page > 0) {
            this._page--;
            this._loadMiniatures();
        }
    }
    _handleNextPage() {
        if (this._hasMore) {
            this._page++;
            this._loadMiniatures();
        }
    }

    _renderList() {
        return html`
            <div class="page-header">
                <h4>Miniatures</h4>
                <button class="btn btn-primary btn-sm" @click="${this._startCreate}">+ Create Miniature</button>
            </div>
            <p class="page-subtitle">
                Manage miniature art assets for PCs and monsters. ${this._total} total${this._includeUploads ? '' : ', app uploads hidden'}.
            </p>

            <div class="d-flex gap-2 mb-3">
                <input type="text" class="form-control form-control-sm" placeholder="Search..." style="max-width: 250px;"
                    .value="${this._searchQuery}" @input="${this._handleSearch}">
                <select class="form-select form-select-sm" style="max-width: 120px;" @change="${this._handleTypeFilter}">
                    <option value="">All types</option>
                    <option value="pc" ?selected="${this._miniatureTypeFilter === 'pc'}">PC</option>
                    <option value="monster" ?selected="${this._miniatureTypeFilter === 'monster'}">Monster</option>
                </select>
                <input type="text" class="form-control form-control-sm" placeholder="Tag..." style="max-width: 150px;"
                    .value="${this._tagFilter}" @change="${this._handleTagFilter}">
                <input type="text" class="form-control form-control-sm" placeholder="Author..." style="max-width: 180px;"
                    aria-label="Filter by author" .value="${this._authorFilter}" @change="${this._handleAuthorFilter}">
                <div class="form-check form-check-inline align-self-center mb-0">
                    <input class="form-check-input" type="checkbox" id="include-uploads"
                        .checked="${this._includeUploads}" @change="${this._handleIncludeUploads}">
                    <label class="form-check-label small text-muted" for="include-uploads">Show app uploads</label>
                </div>
            </div>

            ${
                this._loading
                    ? html`
                <div class="text-center py-4"><div class="spinner-border spinner-border-sm text-primary" role="status"></div></div>
            `
                    : html`
                <table class="table table-hover">
                    <thead>
                        <tr>
                            <th style="width: 40px;"></th>
                            <th>Slug</th>
                            <th>Type</th>
                            <th>Tags</th>
                            <th>Source</th>
                            <th>Author</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${this._miniatures.map(
                            (t) => html`
                            <tr style="cursor: pointer;" @click="${() => this._openDetail(t)}">
                                <td>
                                    ${
                                        isFlattenPending(t)
                                            ? html`<div class="miniature-thumb-sm miniature-thumb-status" title="Processing the image…">
                                                  <span class="spinner-border spinner-border-sm" role="status"></span>
                                              </div>`
                                            : t.flatten_status === 'failed'
                                              ? html`<div class="miniature-thumb-sm miniature-thumb-status failed" title="${t.flatten_error || 'Image processing failed'}">
                                                    <i class="bi bi-exclamation-triangle"></i>
                                                </div>`
                                              : t.image_url
                                                ? html`<img src="${t.image_url}" class="miniature-thumb-sm" alt="">`
                                                : html`<div class="miniature-thumb-sm"></div>`
                                    }
                                </td>
                                <td>
                                    <span style="color: #0d6efd; font-weight: 500;">${t.slug}</span>
                                    ${t.in_catalog ? nothing : html`<span class="badge bg-danger text-white ms-2" style="font-size: 0.7rem;"><i class="bi bi-exclamation-triangle-fill me-1"></i>Unpublished</span>`}
                                </td>
                                <td>
                                    ${t.miniature_type ? html`<span class="badge ${t.miniature_type === 'pc' ? 'bg-primary' : 'bg-danger'}">${t.miniature_type}</span>` : '-'}
                                </td>
                                <td>
                                    <div class="badge-list">
                                        ${(t.tags || []).slice(0, 4).map((tag) => html`<span class="badge bg-light text-dark">${tag}</span>`)}
                                        ${(t.tags || []).length > 4 ? html`<span class="badge bg-light text-dark">+${t.tags.length - 4}</span>` : ''}
                                    </div>
                                </td>
                                <td class="text-muted">${t.image_source || '-'}</td>
                                <td class="text-muted">${authorLabel(t.author) || '-'}</td>
                            </tr>
                        `,
                        )}
                    </tbody>
                </table>

                ${this._miniatures.length === 0 ? html`<p class="text-muted text-center py-3">No miniatures found.</p>` : nothing}

                <div class="d-flex justify-content-between align-items-center">
                    <button class="btn btn-sm btn-outline-secondary" ?disabled="${this._page === 0}" @click="${this._handlePrevPage}">Previous</button>
                    <span class="text-muted small">Page ${this._page + 1}</span>
                    <button class="btn btn-sm btn-outline-secondary" ?disabled="${!this._hasMore}" @click="${this._handleNextPage}">Next</button>
                </div>
            `
            }
        `;
    }

    _renderDetail() {
        const t = this._selectedMiniature;
        if (!t) return nothing;

        return html`
            <a class="back-link" href="#" @click="${(e) => {
                e.preventDefault();
                this._backToList();
            }}">
                <i class="bi bi-arrow-left"></i> Back to Miniatures
            </a>

            <div class="detail-header">
                <div class="detail-header-icon">
                    ${t.image_url ? html`<img src="${t.image_url}" alt="">` : html`<i class="bi bi-person-circle" style="font-size: 1.5rem; color: #999;"></i>`}
                </div>
                <div class="detail-header-info">
                    <h4>${t.slug}</h4>
                    <p>${t.miniature_type || 'untyped'} miniature</p>
                </div>
            </div>

            ${
                isFlattenPending(t)
                    ? html`<div class="alert alert-info d-flex align-items-center gap-2">
                          <span class="spinner-border spinner-border-sm" role="status"></span>
                          Processing the uploaded PSD… the new image appears here when it's ready.
                      </div>`
                    : t.flatten_status === 'failed'
                      ? html`<div class="alert alert-danger">
                            The uploaded PSD could not be processed: ${t.flatten_error || 'unknown error'}
                        </div>`
                      : nothing
            }

            <div class="detail-section">
                <h6>Properties</h6>
                <div class="prop-row">
                    <div class="prop-label">Slug</div>
                    <div class="prop-value"><code>${t.slug}</code></div>
                </div>
                <div class="prop-row">
                    <div class="prop-label">Type</div>
                    <div class="prop-value">
                        ${t.miniature_type ? html`<span class="badge ${t.miniature_type === 'pc' ? 'bg-primary' : 'bg-danger'}">${t.miniature_type}</span>` : 'Not set'}
                    </div>
                </div>
                <div class="prop-row">
                    <div class="prop-label">Size</div>
                    <div class="prop-value">
                        ${
                            this._miniatureSizeName(t.miniature_size)
                                ? html`<span class="badge bg-secondary">${this._miniatureSizeName(t.miniature_size)}</span>`
                                : t.size_cells
                                  ? html`<span class="badge bg-secondary">${this._sizeLabel(t.size_cells)}</span>`
                                  : html`<span class="text-muted">Medium (default)</span>`
                        }
                    </div>
                </div>
                <div class="prop-row">
                    <div class="prop-label">Tags</div>
                    <div class="prop-value">
                        ${
                            t.tags && t.tags.length > 0
                                ? html`<div class="badge-list">${t.tags.map((tag) => html`<span class="badge bg-light text-dark">${tag}</span>`)}</div>`
                                : html`<span class="text-muted">None</span>`
                        }
                    </div>
                </div>
                <div class="prop-row">
                    <div class="prop-label">ID</div>
                    <div class="prop-value"><code class="small">${t.id}</code></div>
                </div>
                <div class="prop-row">
                    <div class="prop-label">Published</div>
                    <div class="prop-value">
                        <div class="form-check form-switch mb-0">
                            <input class="form-check-input" type="checkbox" role="switch" id="in-catalog"
                                .checked="${Boolean(t.in_catalog)}"
                                ?disabled="${this._saving}"
                                @change="${this._toggleCatalog}">
                            <label class="form-check-label" for="in-catalog">
                                ${t.in_catalog ? 'Published' : 'Not published'}
                                <span class="text-muted small d-block">
                                    Freelancers can browse and download every version of its d3 image in the Asset Catalog when it’s published. Apps will also use it to leave out art that isn’t published.
                                </span>
                            </label>
                        </div>
                    </div>
                </div>
            </div>

            <div class="detail-section">
                <h6>Images</h6>
                ${
                    previewRows(this._versions()).length > 0
                        ? html`
                    <div style="display: flex; flex-wrap: wrap; gap: 1.5rem; margin-bottom: 1rem;">
                        ${this._sourceGroups(previewRows(this._versions())).map(
                            ({ source, files, current }) => {
                                const selectedVersion =
                                    this._selectedVersions[source] ?? current.version;
                                const selected =
                                    files.find((f) => f.version === selectedVersion) ?? current;
                                const grid = this._gridSpec(t);
                                return html`
                            <div style="text-align: center;">
                                <miniature-grid-preview
                                    src="${selected.image_url}"
                                    footprint-cells="${grid.footprintCells}"
                                    canvas-cells="${grid.canvasCells}"
                                    cell-px="${grid.cellPx}"
                                    cell-size="40"></miniature-grid-preview>
                                <div style="font-size: 0.75rem; color: #666; margin-top: 0.25rem;">${source} (v${selected.version})</div>
                                <div style="margin-top: 0.5rem; display: flex; align-items: center; gap: 0.25rem; justify-content: center;">
                                    <span style="font-size: 0.7rem; color: #999;">version:</span>
                                    <select class="form-select form-select-sm" style="width: auto; font-size: 0.75rem; padding: 0.15rem 1.5rem 0.15rem 0.4rem;"
                                        @change="${(e) => (this._selectedVersions = { ...this._selectedVersions, [source]: parseInt(e.target.value) })}">
                                        ${files.map(
                                            (f) => html`
                                            <option value="${f.version}" ?selected="${selectedVersion === f.version}">v${f.version}</option>
                                        `,
                                        )}
                                    </select>
                                </div>
                                <div style="margin-top: 0.5rem; display: flex; align-items: center; gap: 0.25rem; justify-content: center;">
                                    <button class="btn btn-outline-secondary btn-sm" style="font-size: 0.7rem; padding: 0.1rem 0.4rem;"
                                        ?disabled="${this._versionDownloading === source}"
                                        @click="${() => this._downloadVersion(source, selected)}">
                                        <i class="bi bi-download"></i> ${this._versionDownloading === source ? 'Downloading...' : 'Download'}
                                    </button>
                                </div>
                            </div>
                        `;
                            },
                        )}
                    </div>
                `
                        : html`<p class="text-muted">No images uploaded.</p>`
                }
                ${
                    this._availableSources.length > 0
                        ? html`
                    <div class="mt-2">
                        <label class="form-label small text-muted">Upload image</label>
                        <div class="d-flex gap-2 align-items-center" style="max-width: 400px;">
                            <select class="form-select form-select-sm" style="max-width: 200px;"
                                @change="${(e) => (this._newSource = e.target.value)}">
                                <option value="">Select source...</option>
                                ${this._availableSources.map(
                                    (s) => html`
                                    <option value="${s.slug}" ?selected="${this._newSource === s.slug}">${s.label}</option>
                                `,
                                )}
                            </select>
                            <input type="file" class="form-control form-control-sm" accept=".psd"
                                ?disabled="${this._saving || !this._newSource}"
                                @change="${this._uploadNewImage}">
                        </div>
                    </div>
                `
                        : nothing
                }
            </div>

            <div class="detail-section">
                <h6>Versions</h6>
                <asset-versions-table .versions="${this._versions()}" ?busy="${this._versionsBusy}" can-pin
                    @delete-versions="${this._deleteVersions}" @set-current="${this._setCurrent}"
                    @set-author="${this._setAuthor}" @pin-all="${this._pinAll}"
                    @download-input="${this._downloadInput}"></asset-versions-table>
            </div>

            <div class="detail-section">
                <h6>Served by apps</h6>
                <p class="text-muted small mb-2">
                    Each app serves what its pin names: a version of a source, or a source's latest. An app with no pin serves d3's latest.
                </p>
                <app-pins-panel asset-id="${t.id}" .versions="${this._versions()}"></app-pins-panel>
            </div>

            <div class="d-flex gap-2">
                <button class="btn btn-outline-primary btn-sm" @click="${this._startEdit}">Edit</button>
                ${
                    this._confirmDelete
                        ? html`
                        <button class="btn btn-danger btn-sm" ?disabled="${this._saving}" @click="${this._deleteMiniature}">
                            ${this._saving ? 'Deleting...' : 'Confirm Delete'}
                        </button>
                        <button class="btn btn-outline-secondary btn-sm" @click="${() => (this._confirmDelete = false)}">Cancel</button>
                    `
                        : html`<button class="btn btn-outline-danger btn-sm" @click="${() => (this._confirmDelete = true)}">Delete</button>`
                }
            </div>
        `;
    }

    _renderCreate() {
        return html`
            <a class="back-link" href="#" @click="${(e) => {
                e.preventDefault();
                this._backToList();
            }}">
                <i class="bi bi-arrow-left"></i> Back to Miniatures
            </a>
            <h4 style="font-weight: 600; margin-bottom: 1.5rem;">Create Miniature</h4>
            <div class="form-panel">
                <form @submit="${this._submitCreate}">
                    <div class="mb-3">
                        <label class="form-label">Slug *</label>
                        <input type="text" class="form-control" required placeholder="e.g. goblin"
                            .value="${this._formData.slug}"
                            @input="${(e) => (this._formData = { ...this._formData, slug: e.target.value })}">
                    </div>
                    <div class="mb-3">
                        <label class="form-label">Type *</label>
                        <select class="form-select"
                            @change="${(e) => (this._formData = { ...this._formData, miniature_type: e.target.value })}">
                            <option value="pc" ?selected="${this._formData.miniature_type === 'pc'}">PC</option>
                            <option value="monster" ?selected="${this._formData.miniature_type === 'monster'}">Monster</option>
                        </select>
                    </div>
                    <div class="mb-3">
                        <label class="form-label">Size</label>
                        <select class="form-select"
                            @change="${(e) => (this._formData = { ...this._formData, miniature_size: e.target.value })}">
                            ${this._miniatureSizeOptions().map(
                                (o) => html`
                                <option value="${o.code}" ?selected="${this._formData.miniature_size === o.code}">${o.name}</option>
                            `,
                            )}
                        </select>
                    </div>
                    <div class="mb-3">
                        <label class="form-label">Tags</label>
                        <input type="text" class="form-control" placeholder="comma-separated"
                            .value="${this._formData.tags}"
                            @input="${(e) => (this._formData = { ...this._formData, tags: e.target.value })}">
                    </div>
                    ${
                        this._availableSources.length > 0
                            ? html`
                        <div class="mb-3">
                            <label class="form-label">Source *</label>
                            <select class="form-select" required
                                @change="${(e) => (this._formData = { ...this._formData, source: e.target.value })}">
                                ${this._availableSources.map(
                                    (s) => html`
                                    <option value="${s.slug}" ?selected="${this._formData.source === s.slug}">${s.label}</option>
                                `,
                                )}
                            </select>
                        </div>
                    `
                            : ''
                    }
                    <div class="mb-3">
                        <label class="form-label">PSD *</label>
                        ${
                            this._miniatureTemplateUrl()
                                ? html`
                            <div class="mb-1">
                                <a href="${this._miniatureTemplateUrl()}" target="_blank" rel="noopener" class="small">
                                    <i class="bi bi-download"></i> Download the ${this._miniatureSizeName(this._formData.miniature_size)} starter PSD (${this._miniatureSizePx()}×${this._miniatureSizePx()})
                                </a>
                            </div>
                        `
                                : nothing
                        }
                        <input type="file" class="form-control" accept=".psd" required
                            @change="${(e) => (this._formData = { ...this._formData, file: e.target.files[0] })}">
                        <div class="form-text">The PSD flattens to the miniature image; author it at ${this._miniatureSizePx()}×${this._miniatureSizePx()} (footprint + overflow ring).</div>
                    </div>
                    <button type="submit" class="btn btn-primary" ?disabled="${this._saving}">
                        ${this._saving ? 'Creating...' : 'Create'}
                    </button>
                </form>
            </div>
        `;
    }

    _renderEdit() {
        return html`
            <a class="back-link" href="#" @click="${(e) => {
                e.preventDefault();
                this._view = 'detail';
            }}">
                <i class="bi bi-arrow-left"></i> Back to ${this._selectedMiniature.slug}
            </a>
            <h4 style="font-weight: 600; margin-bottom: 1.5rem;">Edit ${this._selectedMiniature.slug}</h4>
            <div class="form-panel">
                <form @submit="${this._submitEdit}">
                    <div class="mb-3">
                        <label class="form-label">Type</label>
                        <select class="form-select"
                            @change="${(e) => (this._formData = { ...this._formData, miniature_type: e.target.value })}">
                            <option value="pc" ?selected="${this._formData.miniature_type === 'pc'}">PC</option>
                            <option value="monster" ?selected="${this._formData.miniature_type === 'monster'}">Monster</option>
                        </select>
                    </div>
                    <div class="mb-3">
                        <label class="form-label">Tags</label>
                        <input type="text" class="form-control" placeholder="comma-separated"
                            .value="${this._formData.tags}"
                            @input="${(e) => (this._formData = { ...this._formData, tags: e.target.value })}">
                    </div>
                    <div class="d-flex gap-2">
                        <button type="submit" class="btn btn-primary" ?disabled="${this._saving}">
                            ${this._saving ? 'Saving...' : 'Save Changes'}
                        </button>
                        <button type="button" class="btn btn-outline-secondary" @click="${() => (this._view = 'detail')}">Cancel</button>
                    </div>
                </form>
            </div>
        `;
    }

    render() {
        return html`
            ${this._error ? html`<div class="alert alert-danger">${this._error}</div>` : ''}
            ${this._view === 'list' ? this._renderList() : ''}
            ${this._view === 'detail' ? this._renderDetail() : ''}
            ${this._view === 'create' ? this._renderCreate() : ''}
            ${this._view === 'edit' ? this._renderEdit() : ''}
        `;
    }
}

customElements.define('miniatures-view', MiniaturesView);
